import type {
  QuantFileTableData,
  QuantAnalysisSelection,
  QuantAnalysisSortConfig,
} from '@trinity/chat/api/interfaces/projects.quant-analysis';
import type { BarChart, Bar } from '@trinity/chat/types/chat.chart';
import type { QuickChartData } from '@trinity/chat/types/widget-grid';
import { pathStartsWith } from '@trinity/chat/utils/quant-filter';
import { barToPie, pieToBar } from '../components/Chat/chart.utils';

/** Row name keywords that should always render as integers regardless of `decimalPlaces`. */
export const IntegerRowKeywords = ['Column n'];

/** Rounds numeric strings to the given decimal places (default 0).
 *  Non-numeric strings are returned as-is. Units (%, $, etc.) are preserved. */
export function roundCellValue(value: string, decimalPlaces = 0): string {
  const unitInfo = extractUnit(value);
  let numericStr: string;
  if (unitInfo) {
    numericStr = unitInfo.isPrefix ? value.slice(1) : value.slice(0, value.length - 1);
  } else {
    numericStr = value;
  }

  const num = parseFloat(numericStr);
  if (isNaN(num)) return value;

  const rounded = num.toFixed(decimalPlaces);
  if (!unitInfo) return rounded;
  if (unitInfo.isPrefix) return `${unitInfo.unit}${rounded}`;
  return `${rounded}${unitInfo.unit}`;
}

export type ValueWithUnit = {
  value: number | string;
  unit?: string;
  isPrefix?: boolean;
};

type CreateBarChartParams = {
  categories: string[];
  values: number[];
  xAxisTitle: string;
  yAxisTitle: string;
  records: (string | number)[][];
  n?: number;
};

type CreateMultipleBarChartParams = {
  categories: string[];
  rowNames: string[][];
  records: (string | number)[][];
  xAxisTitle: string;
  seriesToN?: Record<string, number>;
};

/**
 * Rows/columns that contain these keywords should be excluded from charts
 * and ignored when sorting cross tabs. These typically represent aggregation rows
 * like totals, sums, averages, etc.
 */
export const AggregationKeywords = ['SUM', 'Net', 'Column n', 'Base n', 'Average'];

export function extractUnit(val: string | number): { unit: string; isPrefix: boolean } | null {
  if (typeof val !== 'string') return null;

  const trimmed = val.trim();

  // Check for suffix units (%, °, etc.)
  if (trimmed.endsWith('%')) {
    return { unit: '%', isPrefix: false };
  }
  if (trimmed.endsWith('°')) {
    return { unit: '°', isPrefix: false };
  }

  // Check for prefix units ($, €, £, ¥, etc.)
  const prefixMatches = trimmed.match(/^([$€£¥₹]+)/);
  if (prefixMatches) {
    return { unit: prefixMatches[1], isPrefix: true };
  }

  return null;
}

export function formatValueWithUnit({ value, unit, isPrefix }: ValueWithUnit): string {
  if (!unit) return String(value);
  return isPrefix ? `${unit}${value}` : `${value}${unit}`;
}

export function detectUnitFromRecords(
  records: (string | number)[][]
): { unit: string; isPrefix: boolean } | null {
  for (const row of records) {
    for (const val of row) {
      const detected = extractUnit(val);
      if (detected) {
        return detected;
      }
    }
  }
  return null;
}

export function resolveColumnName(
  columnIndex: number,
  originalNames: string[][],
  customNames?: Map<number, string[]>
): string[] {
  if (customNames?.has(columnIndex)) {
    return customNames.get(columnIndex);
  }
  return originalNames[columnIndex] || [];
}

export function resolveRowName(
  rowIndex: number,
  originalNames: string[][],
  customNames?: Map<number, string[]>
): string[] {
  if (customNames?.has(rowIndex)) {
    return customNames.get(rowIndex);
  }
  return originalNames[rowIndex] || [];
}

export function validateSelectionForChart(selection: QuantAnalysisSelection): boolean {
  if (!selection) {
    return true;
  }

  const isValidIndexSet = (values: Set<number>): boolean => {
    for (const value of values) {
      if (!Number.isFinite(value) || value < 0) {
        return false;
      }
    }
    return true;
  };

  if (!isValidIndexSet(selection.rows) || !isValidIndexSet(selection.columns)) {
    return false;
  }

  if (selection.cells.size === 0) {
    return true;
  }

  for (const cellKey of selection.cells.keys()) {
    const dashIndex = cellKey.indexOf('-');
    if (dashIndex === -1) {
      return false;
    }

    const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
    const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

    if (!Number.isFinite(rowIndex) || !Number.isFinite(colIndex)) {
      return false;
    }

    if (rowIndex < 0 || colIndex < 0) {
      return false;
    }
  }

  return true;
}

export function applyCustomNamesToFilteredData(
  filteredData: QuantFileTableData,
  originalData: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuantFileTableData {
  if (!customColumnNames && !customRowNames) {
    return filteredData;
  }

  // Create mapping from original indices to filtered indices
  const originalToFilteredColumnMap = new Map<number, number>();
  const originalToFilteredRowMap = new Map<number, number>();

  for (let origColIndex = 0; origColIndex < originalData.columnNames.length; origColIndex++) {
    const origCol = originalData.columnNames[origColIndex];
    const foundIndex = filteredData.columnNames.findIndex((filteredCol, idx) => {
      // Check if this filtered column corresponds to the original column
      return filteredCol.length === origCol.length && filteredCol.every((val, i) => val === origCol[i]);
    });
    if (foundIndex !== -1 && !originalToFilteredColumnMap.has(origColIndex)) {
      originalToFilteredColumnMap.set(origColIndex, foundIndex);
    }
  }

  for (let origRowIndex = 0; origRowIndex < originalData.rowNames.length; origRowIndex++) {
    const origRow = originalData.rowNames[origRowIndex];
    const foundIndex = filteredData.rowNames.findIndex((filteredRow, idx) => {
      // Check if this filtered row corresponds to the original row
      return filteredRow.length === origRow.length && filteredRow.every((val, i) => val === origRow[i]);
    });
    if (foundIndex !== -1 && !originalToFilteredRowMap.has(origRowIndex)) {
      originalToFilteredRowMap.set(origRowIndex, foundIndex);
    }
  }

  // Apply custom names using the mapping
  const result: QuantFileTableData = {
    ...filteredData,
    columnNames: filteredData.columnNames.map((col, filteredIndex) => {
      // Find original index for this filtered column
      for (const [origIndex, mappedFilteredIndex] of originalToFilteredColumnMap.entries()) {
        if (mappedFilteredIndex === filteredIndex && customColumnNames?.has(origIndex)) {
          return customColumnNames.get(origIndex);
        }
      }
      return col;
    }),
    rowNames: filteredData.rowNames.map((row, filteredIndex) => {
      // Find original index for this filtered row
      for (const [origIndex, mappedFilteredIndex] of originalToFilteredRowMap.entries()) {
        if (mappedFilteredIndex === filteredIndex && customRowNames?.has(origIndex)) {
          return customRowNames.get(origIndex);
        }
      }
      return row;
    }),
  };

  return result;
}

function filterMetadata2DArray<T>(
  arr: T[][] | undefined,
  filteredRowSet: Set<number> | null,
  filteredColSet: Set<number> | null
): T[][] | undefined {
  if (!arr) return undefined;
  return arr
    .map((row, rowIndex) => {
      if (filteredRowSet && !filteredRowSet.has(rowIndex)) return null;
      if (!filteredColSet) return row;
      return row.filter((_, colIndex) => filteredColSet.has(colIndex));
    })
    .filter((row): row is T[] => row !== null);
}

function computeFilteredRowIndices(data: QuantFileTableData, rowNamesFilter: string[][]): Set<number> {
  const indices = new Set<number>();
  for (let i = 0; i < data.rowNames.length; i++) {
    const rowPath = data.rowNames[i];
    if (!rowPath) continue;
    if (rowNamesFilter.some(filterPath => pathStartsWith(rowPath, filterPath))) {
      indices.add(i);
    }
  }
  return indices;
}

function computeFilteredColumnIndices(data: QuantFileTableData, columnNamesFilter: string[][]): Set<number> {
  const indices = new Set<number>();
  for (let i = 0; i < data.columnNames.length; i++) {
    const colPath = data.columnNames[i];
    if (!colPath || colPath.length === 0) continue;
    if (columnNamesFilter.some(filterPath => pathStartsWith(colPath, filterPath))) {
      indices.add(i);
    }
  }
  return indices;
}

function buildFilteredMetadata(
  metadata: QuantFileTableData['metadata'],
  filteredRowSet: Set<number> | null,
  filteredColSet: Set<number> | null
): QuantFileTableData['metadata'] {
  if (!metadata || (!filteredRowSet && !filteredColSet)) return metadata;
  return {
    ...metadata,
    arrowColors: filterMetadata2DArray(metadata.arrowColors, filteredRowSet, filteredColSet),
    arrowDirection: filterMetadata2DArray(metadata.arrowDirection, filteredRowSet, filteredColSet),
    isSignificant: filterMetadata2DArray(metadata.isSignificant, filteredRowSet, filteredColSet),
  };
}

export function filterTableData(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][]
): QuantFileTableData {
  if (columnNamesFilter.length === 0 && rowNamesFilter.length === 0) {
    return data;
  }

  const filteredRowIndicesSet = rowNamesFilter.length > 0
    ? computeFilteredRowIndices(data, rowNamesFilter)
    : new Set<number>();

  const filteredColumnIndicesSet = columnNamesFilter.length > 0
    ? computeFilteredColumnIndices(data, columnNamesFilter)
    : new Set<number>();

  const filteredRowIndices = Array.from(filteredRowIndicesSet).sort((a, b) => a - b);
  const filteredColumnIndices = Array.from(filteredColumnIndicesSet).sort((a, b) => a - b);
  const filteredRowSet = filteredRowIndices.length > 0 ? new Set(filteredRowIndices) : null;
  const filteredColSet = filteredColumnIndices.length > 0 ? new Set(filteredColumnIndices) : null;

  const filteredRecords: string[][] = filterMetadata2DArray(
    data.records,
    filteredRowSet,
    filteredColSet
  ) ?? [];

  return {
    ...data,
    rowNames: filteredRowSet ? data.rowNames.filter((_, i) => filteredRowSet.has(i)) : data.rowNames,
    columnNames: filteredColSet ? data.columnNames.filter((_, i) => filteredColSet.has(i)) : data.columnNames,
    records: filteredRecords,
    metadata: buildFilteredMetadata(data.metadata, filteredRowSet, filteredColSet),
  };
}

export function parseNumericValue(val: string | number | undefined): number {
  try {
    if (val == null) return 0;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const parsed = parseFloat(val.replace(/[^0-9.-]+/g, ''));
      if (isNaN(parsed)) return 0;
      return parsed;
    }
    return 0;
  } catch {
    // If any error occurs during parsing, return 0
    return 0;
  }
}

type ManualSelectionChartParams = {
  chartType: QuickChartData['type'];
  tableData: QuantFileTableData;
  selectedCells: Map<string, boolean>;
  fallbackTitle: string;
  sortConfig?: QuantAnalysisSortConfig | null;
};

type ManualCellEntry = {
  rowIndex: number;
  colIndex: number;
  rowLabel: string;
  columnLabel: string;
  value: number;
};

type ManualSelectionMaps = {
  valueMap: Map<string, ManualCellEntry>;
  rowLabelMap: Map<number, string>;
  columnLabelMap: Map<number, string>;
  rowIndexSet: Set<number>;
  columnIndexSet: Set<number>;
  recordsForUnits: (string | number)[][];
};

function parseManualSelectionCells(
  selectedCells: Map<string, boolean>,
  tableData: QuantFileTableData
): ManualSelectionMaps {
  const valueMap = new Map<string, ManualCellEntry>();
  const rowLabelMap = new Map<number, string>();
  const columnLabelMap = new Map<number, string>();
  const rowIndexSet = new Set<number>();
  const columnIndexSet = new Set<number>();
  const recordsForUnits: (string | number)[][] = [];

  for (const cellKey of selectedCells.keys()) {
    const dashIndex = cellKey.indexOf('-');
    if (dashIndex === -1) continue;

    const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
    const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

    const rowPath = tableData.rowNames[rowIndex] || [];
    const columnPath = tableData.columnNames[colIndex] || [];
    const rawValue = tableData.records[rowIndex]?.[colIndex];
    const numericValue = parseNumericValue(rawValue ?? 0);
    const rowLabel = rowPath.length > 0 ? rowPath.join(' - ') : `Row ${rowIndex + 1}`;
    const columnLabel = columnPath.length > 0 ? columnPath.join(' - ') : `Column ${colIndex + 1}`;

    valueMap.set(`${rowIndex}-${colIndex}`, { rowIndex, colIndex, rowLabel, columnLabel, value: numericValue });
    rowIndexSet.add(rowIndex);
    columnIndexSet.add(colIndex);
    if (!rowLabelMap.has(rowIndex)) rowLabelMap.set(rowIndex, rowLabel);
    if (!columnLabelMap.has(colIndex)) columnLabelMap.set(colIndex, columnLabel);
    recordsForUnits.push([rawValue ?? numericValue]);
  }

  return { valueMap, rowLabelMap, columnLabelMap, rowIndexSet, columnIndexSet, recordsForUnits };
}

function computeOrderedRowIndices(
  rowIndexSet: Set<number>,
  sortConfig: QuantAnalysisSortConfig | null | undefined,
  tableData: QuantFileTableData
): number[] {
  const indices = Array.from(rowIndexSet.values());
  if (!sortConfig) {
    return indices.sort((a, b) => a - b);
  }
  const { columnIndex, direction } = sortConfig;
  return indices.sort((a, b) => {
    const aValue = parseNumericValue(tableData.records[a]?.[columnIndex]);
    const bValue = parseNumericValue(tableData.records[b]?.[columnIndex]);
    if (aValue === bValue) return a - b;
    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  });
}

function buildOrderedEntries(
  orderedRowIndices: number[],
  orderedColumnIndices: number[],
  valueMap: Map<string, ManualCellEntry>
): ManualCellEntry[] {
  const entries: ManualCellEntry[] = [];
  for (const rowIndex of orderedRowIndices) {
    for (const colIndex of orderedColumnIndices) {
      const entry = valueMap.get(`${rowIndex}-${colIndex}`);
      if (entry) entries.push(entry);
    }
  }
  return entries;
}

/** Chart types that can be built directly from a manual cell selection. */
export const SUPPORTED_MANUAL_SELECTION_CHART_TYPES: ReadonlySet<QuickChartData['type']> = new Set([
  'bar',
  'stacked-bar',
  'pie',
  'multiple-clustered-bar',
  'multiple-stacked-bar',
  'progress-bar-widget',
]);

export function createManualSelectionChart({
  chartType,
  tableData,
  selectedCells,
  fallbackTitle,
  sortConfig,
}: ManualSelectionChartParams): QuickChartData | null {
  if (!selectedCells || selectedCells.size === 0) {
    return null;
  }

  const { valueMap, rowLabelMap, columnLabelMap, rowIndexSet, columnIndexSet, recordsForUnits } =
    parseManualSelectionCells(selectedCells, tableData);

  if (valueMap.size === 0) {
    return null;
  }

  const unitInfo = detectUnitFromRecords(recordsForUnits);
  const orderedRowIndices = computeOrderedRowIndices(rowIndexSet, sortConfig, tableData);
  const orderedColumnIndices = Array.from(columnIndexSet.values()).sort((a, b) => a - b);
  const orderedEntries = buildOrderedEntries(orderedRowIndices, orderedColumnIndices, valueMap);

  if (orderedEntries.length === 0) {
    return null;
  }

  const categories = orderedEntries.map((entry) => `${entry.rowLabel} • ${entry.columnLabel}`);
  const values = orderedEntries.map((entry) => entry.value);

  const baseChart: QuickChartData = {
    type: 'bar',
    chart: {
      title: fallbackTitle,
      xAxisTitle: 'Selection',
      yAxisTitle: unitInfo?.unit ?? 'Value',
      indexBy: 'x',
      data: categories.map((category, index) => ({
        x: category,
        y: values[index] ?? 0,
      })),
    },
    options: {
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  };

  if (chartType === 'bar') {
    return baseChart;
  }

  if (chartType === 'stacked-bar') {
    return { ...baseChart, type: 'stacked-bar' };
  }

  if (chartType === 'pie') {
    return transformChartType(baseChart, 'pie');
  }

  if (chartType === 'progress-bar-widget') {
    return buildProgressBarFromEntries(orderedEntries, fallbackTitle, unitInfo);
  }

  return buildMultiSeriesChart(chartType, orderedRowIndices, orderedColumnIndices, valueMap, rowLabelMap, columnLabelMap, fallbackTitle, unitInfo, baseChart);
}

function buildProgressBarFromEntries(
  entries: ManualCellEntry[],
  title: string,
  unitInfo: { unit: string; isPrefix: boolean } | null
): QuickChartData {
  const limitedEntries = entries.slice(0, 6);
  return {
    type: 'progress-bar-widget',
    title,
    backgroundColor: 'var(--gray-xl)',
    values: limitedEntries.map((entry) => String(entry.value)),
    labels: limitedEntries.map((entry) => `${entry.rowLabel} - ${entry.columnLabel}`),
    minValues: limitedEntries.map(() => '0'),
    maxValues: limitedEntries.map(() => '100'),
    units: limitedEntries.map(() => unitInfo?.unit ?? '%'),
  };
}

function buildMultiSeriesChart(
  chartType: QuickChartData['type'],
  orderedRowIndices: number[],
  orderedColumnIndices: number[],
  valueMap: Map<string, ManualCellEntry>,
  rowLabelMap: Map<number, string>,
  columnLabelMap: Map<number, string>,
  fallbackTitle: string,
  unitInfo: { unit: string; isPrefix: boolean } | null,
  baseChart: QuickChartData
): QuickChartData {
  const seriesData: Bar[] = [];

  for (const colIndex of orderedColumnIndices) {
    const seriesLabel = columnLabelMap.get(colIndex);
    if (!seriesLabel) continue;

    const dataPoints: Bar['data'] = [];
    for (const rowIndex of orderedRowIndices) {
      const entry = valueMap.get(`${rowIndex}-${colIndex}`);
      if (!entry) continue;
      const label = rowLabelMap.get(rowIndex) ?? `Row ${rowIndex + 1}`;
      dataPoints.push({ x: label, y: entry.value });
    }

    if (dataPoints.length > 0) {
      seriesData.push({ index: seriesLabel, data: dataPoints });
    }
  }

  if (seriesData.length === 0) {
    return chartType === 'multiple-stacked-bar' ? { ...baseChart, type: 'stacked-bar' } as QuickChartData : baseChart;
  }

  return {
    type: chartType,
    chart: {
      title: fallbackTitle,
      xAxisTitle: orderedRowIndices.length > 1 ? 'Rows' : 'Selection',
      yAxisTitle: unitInfo?.unit ?? 'Values',
      indexBy: 'x',
      data: seriesData,
    },
    options: {
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  } as QuickChartData;
}

function extractColumnN(data: QuantFileTableData): number[] {
  for (let i = 0; i < data.rowNames.length; i++) {
    const row = data.rowNames[i];
    const hasColumnN = row.some((cell: string) => cell?.toLowerCase().includes('column n'));

    if (hasColumnN) {
      return data.records[i].map((val) => parseNumericValue(val));
    }
  }
  return [];
}

function createBarChart({
  categories,
  values,
  xAxisTitle,
  yAxisTitle,
  records,
  n,
}: CreateBarChartParams): QuickChartData {
  const chartData: { x: string; y: number }[] = categories.map((category, index) => ({
    x: category,
    y: values[index] ?? 0,
  }));

  const unitInfo = detectUnitFromRecords(records);

  return {
    type: 'bar' as const,
    chart: {
      title: 'Quantitative Analysis Results',
      xAxisTitle,
      yAxisTitle,
      indexBy: 'x',
      data: chartData,
    } as BarChart,
    options: {
      n,
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  };
}

function createMultipleClusteredBarChart({
  categories,
  rowNames,
  records,
  xAxisTitle,
  seriesToN,
}: CreateMultipleBarChartParams): QuickChartData {
  const unitInfo = detectUnitFromRecords(records);

  const seriesData = categories
    .map((category, colIndex) => {
      if (!category || (typeof category === 'string' && category.trim() === '')) {
        return null;
      }

      const dataPoints = rowNames.map((rowName, rowIndex) => ({
        x: rowName[0] || '',
        y: parseNumericValue(records[rowIndex]?.[colIndex]),
      }));

      if (dataPoints.length === 0) {
        return null;
      }

      return {
        index: category,
        data: dataPoints,
      };
    })
    .filter((series): series is Bar =>
      Boolean(series && Array.isArray(series.data) && series.data.length > 0)
    );

  return {
    type: 'multiple-clustered-bar',
    chart: {
      title: 'Quantitative Analysis Results',
      xAxisTitle,
      yAxisTitle: 'Values',
      indexBy: 'x',
      data: seriesData,
    },
    options: {
      seriesToN,
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  };
}

function createMultipleStackedBarChart({
  categories,
  rowNames,
  records,
  xAxisTitle,
  seriesToN,
}: CreateMultipleBarChartParams): QuickChartData {
  const unitInfo = detectUnitFromRecords(records);

  const seriesData = categories
    .map((category, colIndex) => {
      if (!category || (typeof category === 'string' && category.trim() === '')) {
        return null;
      }

      const dataPoints = rowNames.map((rowName, rowIndex) => ({
        x: rowName[0] || '',
        y: parseNumericValue(records[rowIndex]?.[colIndex]),
      }));

      if (dataPoints.length === 0) {
        return null;
      }

      return {
        index: category,
        data: dataPoints,
      };
    })
    .filter((series): series is Bar =>
      Boolean(series && Array.isArray(series.data) && series.data.length > 0)
    );

  return {
    type: 'multiple-stacked-bar',
    chart: {
      title: 'Quantitative Analysis Results',
      xAxisTitle,
      yAxisTitle: 'Values',
      indexBy: 'x',
      data: seriesData,
    },
    options: {
      seriesToN,
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  };
}

function createMultipleBarChart({
  categories,
  rowNames,
  records,
  xAxisTitle,
  seriesToN,
}: CreateMultipleBarChartParams): QuickChartData {
  // Use stacked as the default if we have many series, it scales better
  const seriesCount = categories.filter((cat) => cat).length;
  const threshold = 4;

  if (seriesCount >= threshold) {
    return createMultipleStackedBarChart({
      categories,
      rowNames,
      records,
      xAxisTitle,
      seriesToN,
    });
  } else {
    return createMultipleClusteredBarChart({
      categories,
      rowNames,
      records,
      xAxisTitle,
      seriesToN,
    });
  }
}

function removeAggregationRowsAndCols(data: QuantFileTableData): {
  filteredData: QuantFileTableData;
  nonAggregationColIndices: number[];
} {
  const nonAggregationRowIndices: number[] = [];
  for (let i = 0; i < data.rowNames.length; i++) {
    if (!isAggregationRow(data.rowNames[i])) {
      nonAggregationRowIndices.push(i);
    }
  }

  const nonAggregationColIndices: number[] = [];
  for (let i = 0; i < data.columnNames.length; i++) {
    const col = data.columnNames[i];
    if (!col.some(cell => AggregationKeywords.some(agg => (cell || '').toLowerCase().includes(agg.toLowerCase())))) {
      nonAggregationColIndices.push(i);
    }
  }

  const filteredData = {
    ...data,
    rowNames: data.rowNames.filter((_, i) => nonAggregationRowIndices.includes(i)),
    columnNames: data.columnNames.filter((_, i) => nonAggregationColIndices.includes(i)),
    records: data.records
      .filter((_, i) => nonAggregationRowIndices.includes(i))
      .map((row) => row.filter((_, i) => nonAggregationColIndices.includes(i))),
  };

  return { filteredData, nonAggregationColIndices };
}

function applyCustomNames(
  data: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuantFileTableData {
  if (!customColumnNames && !customRowNames) return data;
  return {
    ...data,
    columnNames: data.columnNames.map((_, index) =>
      resolveColumnName(index, data.columnNames, customColumnNames)
    ),
    rowNames: data.rowNames.map((_, index) => resolveRowName(index, data.rowNames, customRowNames)),
  };
}

function buildSeriesMapping(categories: string[], columnNValues: number[]): Record<string, number> | undefined {
  if (columnNValues.length === 0) return undefined;
  const mapping: Record<string, number> = {};
  categories.forEach((category, index) => {
    if (index < columnNValues.length && columnNValues[index] > 0) {
      mapping[category] = columnNValues[index]!;
    }
  });
  return Object.keys(mapping).length > 0 ? mapping : undefined;
}

function sortFilteredData(
  filteredData: QuantFileTableData,
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' }
): QuantFileTableData {
  const indexedData = filteredData.records.map((record, index) => ({
    record,
    rowName: filteredData.rowNames[index] ?? [],
  }));

  indexedData.sort((a, b) => {
    const aNum = parseNumericValue(a.record[sortConfig.columnIndex]);
    const bNum = parseNumericValue(b.record[sortConfig.columnIndex]);
    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
  });

  return {
    ...filteredData,
    records: indexedData.map((item) => item.record),
    rowNames: indexedData.map((item) => item.rowName),
  };
}

export function quickChartingRules(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null = null,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
) {
  const dataWithCustomNames = applyCustomNames(data, customColumnNames, customRowNames);
  let filteredData = filterTableData(dataWithCustomNames, columnNamesFilter, rowNamesFilter);

  // Extract column n BEFORE filtering out aggregation rows
  const columnNValues = extractColumnN(filteredData);

  const { filteredData: withoutAggregation, nonAggregationColIndices } = removeAggregationRowsAndCols(filteredData);
  filteredData = withoutAggregation;

  const filteredColumnNValues =
    columnNValues.length > 0 ? columnNValues.filter((_, i) => nonAggregationColIndices.includes(i)) : [];

  // Filter out empty arrays
  filteredData = {
    ...filteredData,
    columnNames: filteredData.columnNames.filter((col) => col && col.length > 0 && col.some((cell) => cell)),
    rowNames: filteredData.rowNames.filter((row) => row && row.length > 0 && row.some((cell) => cell)),
  };

  if (filteredData.columnNames.length === 0 || filteredData.rowNames.length === 0) {
    return null;
  }

  if (sortConfig) {
    filteredData = sortFilteredData(filteredData, sortConfig);
  }

  return generateChartFromData(filteredData, columnNValues, filteredColumnNValues, rowNamesFilter);
}

function tryGenerateSingleRow2LevelBar(
  filteredData: QuantFileTableData,
  columnNValues: number[],
  rowNamesFilter: string[][]
): QuickChartData | null {
  const categories = filteredData.columnNames.map((colPath) => `${colPath[0]} - ${colPath[1]}`);
  const values = filteredData.records[0].map((val) => parseNumericValue(val));
  return createBarChart({
    categories,
    values,
    xAxisTitle: filteredData.columnNames[0]?.[0] ?? '',
    yAxisTitle: rowNamesFilter[0]?.join(' > ') ?? '',
    records: filteredData.records,
    n: columnNValues[0],
  });
}

function tryGenerate3LevelColumnsBar(
  filteredData: QuantFileTableData,
  filteredColumnNValues: number[]
): QuickChartData | null {
  const categories = filteredData.columnNames.map((colPath) => `${colPath[1]} - ${colPath[2]}`);
  return createMultipleBarChart({
    categories,
    rowNames: filteredData.rowNames,
    records: filteredData.records,
    xAxisTitle: filteredData.columnNames[0]?.[1] ?? '',
    seriesToN: buildSeriesMapping(categories, filteredColumnNValues),
  });
}

function tryGenerateMultiRow2LevelBar(
  filteredData: QuantFileTableData,
  filteredColumnNValues: number[]
): QuickChartData | null {
  const categories = filteredData.columnNames.map((colPath) => `${colPath[0]} - ${colPath[1]}`);
  return createMultipleBarChart({
    categories,
    rowNames: filteredData.rowNames,
    records: filteredData.records,
    xAxisTitle: filteredData.columnNames[0]?.[0] ?? '',
    seriesToN: buildSeriesMapping(categories, filteredColumnNValues),
  });
}

function tryGenerateMultiRow1LevelBar(
  filteredData: QuantFileTableData,
  columnNValues: number[]
): QuickChartData | null {
  const categories = filteredData.rowNames.map((colPath) => colPath[0] ?? '');
  const values = filteredData.records.map((row) => parseNumericValue(row[0]));
  return createBarChart({
    categories,
    values,
    xAxisTitle: filteredData.columnNames[0]?.[0] ?? '',
    yAxisTitle: 'Values',
    records: filteredData.records,
    n: columnNValues[0],
  });
}

function tryGenerateKpiCard(filteredData: QuantFileTableData): QuickChartData | null {
  const rowLabel = filteredData.rowNames[0]?.[0] ?? '';
  const colLabel = filteredData.columnNames[0]?.[0] ?? '';
  const value = filteredData.records[0]?.[0] ?? '';
  const title = `${rowLabel} vs. ${colLabel}`;
  return {
    type: 'kpi-widget' as const,
    title,
    backgroundColor: 'var(--gray-xl)',
    values: [String(value || '')],
    labels: [title],
  };
}

function isAllDataZero(records: string[][]): boolean {
  return records.every((row) => row.every((val) => parseNumericValue(val) === 0));
}

function hasMultipleColumnParents(filteredData: QuantFileTableData): boolean {
  const columnParentCounts = countParentOccurrences(filteredData.columnNames);
  return Object.keys(columnParentCounts).length > 1;
}

function matchChartPattern(
  filteredData: QuantFileTableData,
  columnHierarchy: number,
  rowHierarchy: number,
  columnNValues: number[],
  filteredColumnNValues: number[],
  rowNamesFilter: string[][]
): QuickChartData | null {
  if (rowHierarchy === 1 && columnHierarchy === 2 && filteredData.records.length === 1) {
    return tryGenerateSingleRow2LevelBar(filteredData, columnNValues, rowNamesFilter);
  }

  if (rowHierarchy === 1 && columnHierarchy === 3) {
    return tryGenerate3LevelColumnsBar(filteredData, filteredColumnNValues);
  }

  if (rowHierarchy === 1 && columnHierarchy === 2 && filteredData.records.length > 1) {
    return tryGenerateMultiRow2LevelBar(filteredData, filteredColumnNValues);
  }

  if (rowHierarchy === 1 && columnHierarchy === 1 && filteredData.columnNames.length === 1 && filteredData.rowNames.length > 1) {
    return tryGenerateMultiRow1LevelBar(filteredData, columnNValues);
  }

  if (filteredData.columnNames.length === 1 && filteredData.rowNames.length === 1) {
    return tryGenerateKpiCard(filteredData);
  }

  return null;
}

function generateChartFromData(
  filteredData: QuantFileTableData,
  columnNValues: number[],
  filteredColumnNValues: number[],
  rowNamesFilter: string[][]
): QuickChartData | null {
  const columnHierarchy = filteredData.columnNames[0]?.length || 0;
  const rowHierarchy = filteredData.rowNames[0]?.length || 0;

  if (rowHierarchy > 1 || columnHierarchy > 3) return null;
  if (isAllDataZero(filteredData.records)) return null;
  if (columnHierarchy === 3 && hasMultipleColumnParents(filteredData)) return null;

  return matchChartPattern(filteredData, columnHierarchy, rowHierarchy, columnNValues, filteredColumnNValues, rowNamesFilter);
}

function countParentOccurrences(data: string[][]): Record<string, number> {
  const parentCounts: Record<string, number> = {};
  data.forEach((path) => {
    const parent = path[0] ?? '';
    parentCounts[parent] = (parentCounts[parent] || 0) + 1;
  });
  return parentCounts;
}

export function transformChartType(chart: QuickChartData, newType: string): QuickChartData {
  if (chart.type === 'bar' && newType === 'pie') {
    return {
      type: 'pie' as const,
      chart: barToPie(chart.chart),
    };
  }

  if (chart.type === 'pie' && newType === 'bar') {
    return {
      type: 'bar' as const,
      chart: pieToBar(chart.chart),
    };
  }

  return {
    ...chart,
    type: newType,
  } as QuickChartData;
}

export function convertColorIntToHex(colorInt: number): string {
  const unsignedColor = (colorInt >>> 0) & 0xffffff;
  return '#' + unsignedColor.toString(16).padStart(6, '0').toUpperCase();
}

function isAggregationRow(row: string[]): boolean {
  return row.some(cell =>
    AggregationKeywords.some(agg => (cell || '').toLowerCase().includes(agg.toLowerCase()))
  );
}

function getSortedRowNames(
  tableData: QuantFileTableData,
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' }
): string[][] {
  const rowCount = tableData.rowNames.length;
  const nonAggregationData: Array<{ rowName: string[]; record: (string | number)[] }> = [];
  const aggregationData: Array<{ rowName: string[] }> = [];

  for (let i = 0; i < rowCount; i++) {
    const row = tableData.rowNames[i];
    if (!row) continue;
    if (isAggregationRow(row)) {
      aggregationData.push({ rowName: row });
    } else {
      nonAggregationData.push({ rowName: row, record: tableData.records[i] ?? [] });
    }
  }

  const sortDir = sortConfig.direction === 'asc' ? 1 : -1;
  nonAggregationData.sort((a, b) => {
    const aNum = parseNumericValue(a.record[sortConfig.columnIndex]);
    const bNum = parseNumericValue(b.record[sortConfig.columnIndex]);
    return (aNum - bNum) * sortDir;
  });

  return [
    ...nonAggregationData.map(d => d.rowName),
    ...aggregationData.map(d => d.rowName),
  ];
}

function collectCellPaths(
  cells: Map<string, boolean>,
  tableData: QuantFileTableData
): { rowPathSet: Map<string, string[]>; columnPathSet: Map<string, string[]> } {
  const rowPathSet = new Map<string, string[]>();
  const columnPathSet = new Map<string, string[]>();

  for (const cellKey of cells.keys()) {
    const dashIndex = cellKey.indexOf('-');
    if (dashIndex === -1) continue;

    const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
    const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

    const rowPath = tableData.rowNames[rowIndex];
    if (rowPath) {
      const key = rowPath.join('||');
      if (!rowPathSet.has(key)) rowPathSet.set(key, rowPath);
    }

    const columnPath = tableData.columnNames[colIndex];
    if (columnPath) {
      const key = columnPath.join('||');
      if (!columnPathSet.has(key)) columnPathSet.set(key, columnPath);
    }
  }

  return { rowPathSet, columnPathSet };
}

function processSelectedRows(
  selection: QuantAnalysisSelection,
  sortedRowNames: string[][],
  rowPathSet: Map<string, string[]>
): string[] {
  const rowNames: string[] = [];
  const rowNamesSet = new Set<string>();
  const rowCount = sortedRowNames.length;

  for (const rowIndex of selection.rows) {
    if (rowIndex < 0 || rowIndex >= rowCount) continue;
    const rowName = sortedRowNames[rowIndex];
    if (!rowName) continue;

    const rowKey = rowName.join('||');
    if (!rowPathSet.has(rowKey)) rowPathSet.set(rowKey, rowName);

    const firstLevel = rowName[0];
    if (firstLevel && !rowNamesSet.has(firstLevel)) {
      rowNamesSet.add(firstLevel);
      rowNames.push(firstLevel);
    }
  }

  return rowNames;
}

function processSelectedColumns(
  selection: QuantAnalysisSelection,
  tableData: QuantFileTableData,
  columnPathSet: Map<string, string[]>
): string[] {
  const columnNames: string[] = [];
  const columnNamesSet = new Set<string>();
  const colCount = tableData.columnNames.length;

  for (const colIndex of selection.columns) {
    if (colIndex < 0 || colIndex >= colCount) continue;
    const colName = tableData.columnNames[colIndex];
    if (!colName || colName.length === 0) continue;

    const columnKey = colName.join('||');
    if (!columnPathSet.has(columnKey)) columnPathSet.set(columnKey, colName);

    let filterName: string;
    if (colName.length === 1) {
      filterName = colName[0] ?? '';
    } else {
      filterName = colName.join(' - ');
      if (columnNamesSet.has(filterName)) {
        filterName = colName[colName.length - 1] ?? '';
      }
    }

    if (!columnNamesSet.has(filterName)) {
      columnNamesSet.add(filterName);
      columnNames.push(filterName);
    }
  }

  return columnNames;
}

function mergeCellPathsInto(
  cells: Map<string, boolean>,
  tableData: QuantFileTableData,
  rowPathSet: Map<string, string[]>,
  columnPathSet: Map<string, string[]>,
) {
  const { rowPathSet: cellRowPaths, columnPathSet: cellColPaths } = collectCellPaths(cells, tableData);
  for (const [key, value] of cellRowPaths) {
    if (!rowPathSet.has(key)) rowPathSet.set(key, value);
  }
  for (const [key, value] of cellColPaths) {
    if (!columnPathSet.has(key)) columnPathSet.set(key, value);
  }
}

export function convertSelectionToFilters(
  selection: QuantAnalysisSelection,
  tableData: QuantFileTableData | null,
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null = null
): {
  columnNames: string[];
  rowNames: string[];
  selectedCells?: Map<string, boolean>;
  columnPaths: string[][];
  rowPaths: string[][];
} {
  if (!tableData) {
    return { columnNames: [], rowNames: [], columnPaths: [], rowPaths: [] };
  }

  if (selection.cells.size > 0 && selection.rows.size === 0 && selection.columns.size === 0) {
    const { rowPathSet, columnPathSet } = collectCellPaths(selection.cells, tableData);
    return {
      columnNames: [],
      rowNames: [],
      selectedCells: new Map(selection.cells),
      columnPaths: Array.from(columnPathSet.values()),
      rowPaths: Array.from(rowPathSet.values()),
    };
  }

  const columnPathSet = new Map<string, string[]>();
  const rowPathSet = new Map<string, string[]>();

  const sortedRowNames = (sortConfig && selection.rows.size > 0)
    ? getSortedRowNames(tableData, sortConfig)
    : tableData.rowNames;

  const rowNames = selection.rows.size > 0
    ? processSelectedRows(selection, sortedRowNames, rowPathSet)
    : [];

  const columnNames = selection.columns.size > 0
    ? processSelectedColumns(selection, tableData, columnPathSet)
    : [];

  if (selection.cells.size > 0) {
    mergeCellPathsInto(selection.cells, tableData, rowPathSet, columnPathSet);
  }

  return {
    columnNames,
    rowNames,
    selectedCells: selection.cells.size > 0 ? new Map(selection.cells) : undefined,
    columnPaths: Array.from(columnPathSet.values()),
    rowPaths: Array.from(rowPathSet.values()),
  };
}

function formatColPathCategory(colPath: string[]): string {
  if (colPath.length === 1) return colPath[0];
  if (colPath.length === 2) return `${colPath[0]} - ${colPath[1]}`;
  if (colPath.length === 3) return `${colPath[1]} - ${colPath[2]}`;
  return colPath.join(' - ');
}

function tryGenerateMultipleClusteredBar(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  const filteredData = filterTableData(data, columnNamesFilter, rowNamesFilter);

  if (!filteredData || filteredData.records.length === 0 || filteredData.columnNames.length === 0) {
    return null;
  }

  if (filteredData.records.length === 1 && filteredData.columnNames.length > 1) {
    const categories = filteredData.columnNames.map(formatColPathCategory);
    const xAxisTitle = filteredData.columnNames[0]?.[0] || 'Categories';
    const columnNValues = extractColumnN(filteredData);

    return createMultipleBarChart({
      categories,
      rowNames: filteredData.rowNames,
      records: filteredData.records,
      xAxisTitle,
      seriesToN: buildSeriesMapping(categories, columnNValues),
    });
  }

  const result = quickChartingRules(
    data,
    columnNamesFilter,
    rowNamesFilter,
    sortConfig,
    customColumnNames,
    customRowNames
  );

  if (result && result.type === 'multiple-clustered-bar') {
    return result;
  }

  return null;
}

function tryGenerateMultipleStackedBar(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  const result = quickChartingRules(
    data,
    columnNamesFilter,
    rowNamesFilter,
    sortConfig,
    customColumnNames,
    customRowNames
  );

  if (result && result.type === 'multiple-clustered-bar') {
    return {
      ...result,
      type: 'multiple-stacked-bar',
    };
  }

  return null;
}

function getRowLabel(filteredData: QuantFileTableData, rowIndex: number): string {
  const rowName = filteredData.rowNames[rowIndex];
  return Array.isArray(rowName) ? rowName.join(' - ') : String(rowName || `Row ${rowIndex + 1}`);
}

function getColLabel(filteredData: QuantFileTableData, colIndex: number): string {
  const colName = colIndex < filteredData.columnNames.length ? filteredData.columnNames[colIndex] : undefined;
  return Array.isArray(colName) ? colName.join(' - ') : String(colName || `Col ${colIndex + 1}`);
}

type CellValue = { value: string; numericValue: number; label: string };

function parseCellKeyIndices(cellKey: string): { rowIndex: number; colIndex: number } | null {
  const dashIndex = cellKey.indexOf('-');
  if (dashIndex === -1) return null;
  const rowIndex = parseInt(cellKey.substring(0, dashIndex), 10);
  const colIndex = parseInt(cellKey.substring(dashIndex + 1), 10);
  if (isNaN(rowIndex) || isNaN(colIndex)) return null;
  return { rowIndex, colIndex };
}

function buildCellValue(
  filteredData: QuantFileTableData,
  rowIndex: number,
  colIndex: number,
  useNumeric: boolean
): CellValue {
  const raw = filteredData.records[rowIndex]?.[colIndex];
  const numericValue = parseNumericValue(raw ?? 0);
  const value = useNumeric ? String(numericValue) : String(raw || '');
  const label = `${getRowLabel(filteredData, rowIndex)} - ${getColLabel(filteredData, colIndex)}`;
  return { value, numericValue, label };
}

function collectFromSelectedCells(
  filteredData: QuantFileTableData,
  selectedCells: Map<string, boolean>,
  maxItems: number,
  useNumeric: boolean
): CellValue[] {
  const results: CellValue[] = [];
  for (const cellKey of selectedCells.keys()) {
    if (results.length >= maxItems) break;
    const indices = parseCellKeyIndices(cellKey);
    if (!indices) continue;
    const { rowIndex, colIndex } = indices;
    if (rowIndex < 0 || rowIndex >= filteredData.records.length) continue;
    const row = filteredData.records[rowIndex];
    if (!row || colIndex < 0 || colIndex >= row.length) continue;
    results.push(buildCellValue(filteredData, rowIndex, colIndex, useNumeric));
  }
  return results;
}

function collectFromAllCells(
  filteredData: QuantFileTableData,
  maxItems: number,
  useNumeric: boolean
): CellValue[] {
  const results: CellValue[] = [];
  for (let rowIndex = 0; rowIndex < filteredData.records.length && results.length < maxItems; rowIndex++) {
    const row = filteredData.records[rowIndex];
    if (!row) continue;
    for (let colIndex = 0; colIndex < row.length && results.length < maxItems; colIndex++) {
      results.push(buildCellValue(filteredData, rowIndex, colIndex, useNumeric));
    }
  }
  return results;
}

function collectCellValues(
  filteredData: QuantFileTableData,
  selectedCells: Map<string, boolean> | undefined,
  maxItems: number,
  useNumeric: boolean
): CellValue[] {
  if (selectedCells && selectedCells.size > 0) {
    return collectFromSelectedCells(filteredData, selectedCells, maxItems, useNumeric);
  }
  return collectFromAllCells(filteredData, maxItems, useNumeric);
}

function prepareWidgetData(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuantFileTableData | null {
  const dataToUse = (!filteredTableData && (customColumnNames || customRowNames))
    ? applyCustomNames(data, customColumnNames, customRowNames)
    : data;

  const filteredData = filteredTableData || filterTableData(dataToUse, columnNamesFilter, rowNamesFilter);
  if (!filteredData?.records || filteredData.records.length === 0) return null;
  return filteredData;
}

/**
 * Generates a KPI widget from table data.
 * Can be used with selected cells or will iterate over all data as fallback.
 */
export function tryGenerateKpiWidget(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  chartTitle: string,
  defaultColor: string,
  selectedCells?: Map<string, boolean>,
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  const filteredData = prepareWidgetData(data, columnNamesFilter, rowNamesFilter, filteredTableData, customColumnNames, customRowNames);
  if (!filteredData) return null;

  const cellValues = collectCellValues(filteredData, selectedCells, 6, false);
  if (cellValues.length === 0) return null;

  return {
    type: 'kpi-widget',
    title: chartTitle,
    backgroundColor: defaultColor,
    values: cellValues.map(c => c.value),
    labels: cellValues.map(c => c.label),
  };
}

/**
 * Generates a Progress Bar widget from table data.
 * Can be used with selected cells or will iterate over all data as fallback.
 */
export function tryGenerateProgressBarWidget(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  chartTitle: string,
  defaultColor: string,
  selectedCells?: Map<string, boolean>,
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  const filteredData = prepareWidgetData(data, columnNamesFilter, rowNamesFilter, filteredTableData, customColumnNames, customRowNames);
  if (!filteredData) return null;

  const cellValues = collectCellValues(filteredData, selectedCells, 6, true);
  if (cellValues.length === 0) return null;

  return {
    type: 'progress-bar-widget',
    title: chartTitle,
    backgroundColor: defaultColor,
    values: cellValues.map(c => c.value),
    labels: cellValues.map(c => c.label),
    minValues: cellValues.map(() => '0'),
    maxValues: cellValues.map(() => '100'),
    units: cellValues.map(() => '%'),
  };
}

function tryGenerateDataTable(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  chartTitle: string,
  defaultColor: string,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  const dataWithCustomNames = applyCustomNames(data, customColumnNames, customRowNames);

  const filteredData = filterTableData(dataWithCustomNames, columnNamesFilter, rowNamesFilter);

  if (!filteredData || filteredData.columnNames.length === 0 || filteredData.rowNames.length === 0) {
    return null;
  }

  return {
    type: 'data-table',
    title: chartTitle || 'Data Table',
    headerColor: defaultColor,
    data: structuredClone(filteredData),
    sortConfig,
  };
}

export function tryGenerateChartType(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  chartType:
    | 'multiple-clustered-bar'
    | 'multiple-stacked-bar'
    | 'kpi-widget'
    | 'data-table'
    | 'progress-bar-widget',
  chartTitle = 'Quantitative Analysis',
  defaultColor = 'var(--gray-xl)',
  selectedCells?: Map<string, boolean>,
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  switch (chartType) {
    case 'multiple-clustered-bar':
      return tryGenerateMultipleClusteredBar(
        data,
        columnNamesFilter,
        rowNamesFilter,
        sortConfig,
        customColumnNames,
        customRowNames
      );
    case 'multiple-stacked-bar':
      return tryGenerateMultipleStackedBar(
        data,
        columnNamesFilter,
        rowNamesFilter,
        sortConfig,
        customColumnNames,
        customRowNames
      );
    case 'kpi-widget':
      return tryGenerateKpiWidget(
        data,
        columnNamesFilter,
        rowNamesFilter,
        chartTitle,
        defaultColor,
        selectedCells,
        filteredTableData,
        customColumnNames,
        customRowNames
      );
    case 'progress-bar-widget':
      return tryGenerateProgressBarWidget(
        data,
        columnNamesFilter,
        rowNamesFilter,
        chartTitle,
        defaultColor,
        selectedCells,
        filteredTableData,
        customColumnNames,
        customRowNames
      );
    case 'data-table':
      return tryGenerateDataTable(
        data,
        columnNamesFilter,
        rowNamesFilter,
        sortConfig,
        chartTitle,
        defaultColor,
        customColumnNames,
        customRowNames
      );
    default:
      return null;
  }
}

export function generateChartWithPriority(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  chartTitle = 'Quantitative Analysis',
  defaultColor = 'var(--gray-xl)',
  selectedCells?: Map<string, boolean>,
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  // If we have a small, explicit cell selection (up to 6 cells),
  // always prefer a KPI widget (metric card).
  if (selectedCells && selectedCells.size > 0 && selectedCells.size <= 6) {
    const kpi = tryGenerateKpiWidget(
      data,
      columnNamesFilter,
      rowNamesFilter,
      chartTitle,
      defaultColor,
      selectedCells,
      filteredTableData,
      customColumnNames,
      customRowNames
    );
    if (kpi) {
      return kpi;
    }
  }

  const chartTypes: Array<'multiple-clustered-bar' | 'multiple-stacked-bar' | 'kpi-widget' | 'data-table'> = [
    'multiple-clustered-bar',
    'multiple-stacked-bar',
    'kpi-widget',
    'data-table',
  ];

  for (const chartType of chartTypes) {
    const result = tryGenerateChartType(
      data,
      columnNamesFilter,
      rowNamesFilter,
      sortConfig,
      chartType,
      chartTitle,
      defaultColor,
      selectedCells,
      filteredTableData,
      customColumnNames,
      customRowNames
    );
    if (result) {
      return result;
    }
  }

  return null;
}
